<template>
	<PageTitleComponent />

	<div class="form-container">
		<form
			action="#"
			@submit.prevent="handleLogin"
		>
			<h2>Log in to the Partner Portal</h2>
			<div class="fields">
				<div class="field">
					<input
						name="username"
						autocomplete="username"
						v-model="username"
						type="text"
						placeholder="Username or email"
						required
					/>
				</div>
				<div class="field">
					<input
						name="password"
						autocomplete="current-password"
						v-model="password"
						type="password"
						placeholder="Password"
						required
					/>
				</div>
				<div
					class="field"
					v-if="showLogInAs"
				>
					<input
						name="logInAs"
						autocomplete="off"
						v-model="logInAs"
						type="text"
						placeholder="Log In As"
					/>
				</div>
			</div>
			<div class="error-container">
				<div
					class="error"
					v-if="error"
				>
					Your username or password was incorrect. Please try again or contact your Expert Facilitator.
				</div>
			</div>
			<button
				class="btn btn-primary"
				type="submit"
				:disabled="!isFormValid || loginLoading"
			>
				{{ loginLoading ? 'Logging in...' : 'Login' }}
			</button>

			<div class="forgot-password-link">
				<small>
					<router-link
						:to="{
							name: 'forgot-password',
						}"
						>Forgot Password?</router-link
					>
				</small>
			</div>

			<LoadingComponent :loading="loginLoading" />
		</form>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import PageTitleComponent from '@/components/shared/PageTitleComponent.vue';
import { useUser } from '@/stores/userStore';
import { mapActions, mapState } from 'pinia';
import LoadingComponent from '@/components/shared/LoadingComponent.vue';

// TODO
// This component is very similar to the PasswordResetComponent and LoginComponent.
// Consider refactoring to share common logic and structure.
export default defineComponent({
	components: { PageTitleComponent, LoadingComponent },
	data() {
		return {
			username: '',
			password: '',
			error: false,
			logInAs: '',
			cmdPresses: 0,
		};
	},
	computed: {
		isFormValid(): boolean {
			return this.username.length > 0 && this.password.length > 0;
		},
		showLogInAs() {
			return this.cmdPresses >= 5;
		},
		...mapState(useUser, ['loginLoading']),
	},
	mounted() {
		this.handleDebugKeyPresses();
		this.handlePrefills();
	},
	beforeUnmount() {
		document.onkeydown = null;
	},
	methods: {
		handleDebugKeyPresses() {
			document.onkeydown = (e) => {
				if (this.cmdPresses >= 5) {
					return;
				}

				if (e.metaKey) {
					this.cmdPresses++;
				} else {
					this.cmdPresses = 0;
				}
			};
		},
		handlePrefills() {
			const params = new URLSearchParams(window.location.search);
			const username = params.get('username');
			const password = params.get('password');
			if (username) {
				this.username = username;
			}
			if (password) {
				this.password = password;
			}

			if (username || password) {
				const newQuery = { ...this.$route.query };
				delete newQuery.username;
				delete newQuery.password;

				this.$router.push({
					path: this.$route.path,
					query: newQuery,
					replace: true,
				});
			}

			if (username && password) {
				this.handleLogin();
			}
		},
		async handleLogin() {
			if (this.username === '' || this.password === '') {
				this.error = true;
				return;
			}

			const success = await this.login(
				{
					username: this.username,
					password: this.password,
				},
				this.logInAs
			);

			this.error = !success;
		},
		...mapActions(useUser, ['login']),
	},
});
</script>

<style scoped lang="scss">
.form-container {
	display: flex;
	flex: 1;
	justify-content: center;
	align-items: center;
	margin-bottom: 100px;
}

form {
	background-color: rgb(var(--rs-color-brand-navy));
	padding: var(--rs-layout-content-padding);
	width: clamp(200px, 400px, 100%);

	h2 {
		margin-bottom: 30px;
		color: white;
		font-size: 24px;
	}

	.fields {
		.field {
			margin-bottom: 20px;
		}
	}

	.error-container {
		padding-bottom: 20px;

		.error {
			color: white;
		}
	}

	.forgot-password-link {
		margin-top: 20px;

		a {
			color: white;
			text-decoration: none;

			&:hover {
				text-decoration: underline;
			}
		}
	}
}
</style>
